import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Avatar, Box, Button, Card, CardContent, CircularProgress,
  Divider, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { FairDecision, StudentProfile } from '../../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

const now       = new Date()
const FALL_YEAR = now.getMonth() < 7 ? now.getFullYear() : now.getFullYear() + 1

const EMPTY_ADDRESS = {
  address_line1: '', address_line2: '', address_city: '', address_state: '', address_zip: '',
}

const ADDRESS_FIELDS: {
  key: keyof typeof EMPTY_ADDRESS; label: string; required: boolean; autoComplete: string; span: string
}[] = [
  { key: 'address_line1', label: 'Street address',    required: true,  autoComplete: 'address-line1',  span: '1 / -1' },
  { key: 'address_line2', label: 'Apt, suite, unit',  required: false, autoComplete: 'address-line2',  span: '1 / -1' },
  { key: 'address_city',  label: 'City',              required: true,  autoComplete: 'address-level2', span: 'auto' },
  { key: 'address_state', label: 'State',             required: true,  autoComplete: 'address-level1', span: 'auto' },
  { key: 'address_zip',   label: 'ZIP',               required: true,  autoComplete: 'postal-code',    span: 'auto' },
]

function formatAddress(p: StudentProfile) {
  const street = [p.address_line1, p.address_line2].filter(Boolean).join(', ')
  return `${street}\n${p.address_city ?? ''}, ${p.address_state ?? ''} ${p.address_zip ?? ''}`
}

function gradeLabel(g: number) {
  return `Grade ${g} (Fall ${FALL_YEAR})`
}

function FairToggle({
  value, onChange,
}: {
  value: FairDecision | null
  onChange: (v: FairDecision | null) => void
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, v) => onChange(v)}
      sx={{ mt: 0.5 }}
    >
      <ToggleButton value="yes">Yes</ToggleButton>
      <ToggleButton value="no">No</ToggleButton>
      <ToggleButton value="not_sure">Not sure</ToggleButton>
    </ToggleButtonGroup>
  )
}

// ── Admin / staff profile ─────────────────────────────────────────────────────

function AdminProfile() {
  const { session, profile } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)

  const [firstName,   setFirstName]   = useState(profile?.first_name ?? '')
  const [lastName,    setLastName]    = useState(profile?.last_name  ?? '')
  const [email,       setEmail]       = useState(profile?.email      ?? '')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPwd,  setConfirmPwd]  = useState('')
  const [avatarUrl,   setAvatarUrl]   = useState<string | null>(profile?.avatar_url ?? null)

  const [saving,    setSaving]    = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [success,   setSuccess]   = useState<string | null>(null)

  const initials = `${profile?.first_name?.[0] ?? ''}${profile?.last_name?.[0] ?? ''}`

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !session) return
    setUploading(true)
    setError(null)
    try {
      const ext  = file.name.split('.').pop()
      const path = `users/${session.user.id}/avatar.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true })
      if (uploadErr) throw new Error(uploadErr.message)

      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)
      setAvatarUrl(publicUrl)
      await supabase.from('users').update({ avatar_url: publicUrl }).eq('user_id', session.user.id)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  async function handleSaveName() {
    if (!session) return
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.')
      return
    }
    setSaving(true)
    setError(null)
    setSuccess(null)
    const { error: err } = await supabase
      .from('users')
      .update({ first_name: firstName.trim(), last_name: lastName.trim() })
      .eq('user_id', session.user.id)
    setSaving(false)
    if (err) setError(err.message)
    else setSuccess('Name updated.')
  }

  async function handleSaveEmail() {
    if (!email.trim()) { setError('Email is required.'); return }
    setSaving(true)
    setError(null)
    setSuccess(null)
    const { error: err } = await supabase.auth.updateUser({ email: email.trim().toLowerCase() })
    setSaving(false)
    if (err) setError(err.message)
    else setSuccess('Confirmation sent to your new email address.')
  }

  async function handleSavePassword() {
    if (!newPassword) { setError('Enter a new password.'); return }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (newPassword !== confirmPwd) { setError('Passwords do not match.'); return }
    setSaving(true)
    setError(null)
    setSuccess(null)
    const { error: err } = await supabase.auth.updateUser({ password: newPassword })
    setSaving(false)
    if (err) setError(err.message)
    else { setNewPassword(''); setConfirmPwd(''); setSuccess('Password updated.') }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 580 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>My Profile</Typography>

      {error   && <Alert severity="error"   sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Avatar ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={avatarUrl ?? undefined}
              sx={{ width: 80, height: 80, fontSize: 28, bgcolor: 'primary.main' }}
            >
              {!avatarUrl && initials}
            </Avatar>
            {uploading && (
              <CircularProgress size={80} sx={{ position: 'absolute', top: 0, left: 0 }} />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {profile?.first_name} {profile?.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textTransform: 'capitalize' }}>
              {profile?.role?.replace('_', ' ')}
            </Typography>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
            <Button
              size="small"
              startIcon={<PhotoCameraIcon />}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {avatarUrl ? 'Change photo' : 'Upload photo'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Name ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Name</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="First name"
              size="small"
              fullWidth
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
            />
            <TextField
              label="Last name"
              size="small"
              fullWidth
              value={lastName}
              onChange={e => setLastName(e.target.value)}
            />
          </Box>
          <Button variant="contained" size="small" onClick={handleSaveName} disabled={saving}>
            Save name
          </Button>
        </CardContent>
      </Card>

      {/* ── Email ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Email</Typography>
          <TextField
            label="Email address"
            type="email"
            size="small"
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" size="small" onClick={handleSaveEmail} disabled={saving}>
            Update email
          </Button>
        </CardContent>
      </Card>

      {/* ── Password ── */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Password</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <TextField
              label="New password"
              type="password"
              size="small"
              fullWidth
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
            <TextField
              label="Confirm new password"
              type="password"
              size="small"
              fullWidth
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
            />
          </Box>
          <Button variant="contained" size="small" onClick={handleSavePassword} disabled={saving}>
            Change password
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

// ── Student profile ───────────────────────────────────────────────────────────

interface ParentContact {
  first_name: string | null
  last_name:  string | null
  email:      string
  phone:      string | null
}

interface MentorContact {
  user_id:    string
  first_name: string | null
  last_name:  string | null
  email:      string
  avatar_url: string | null
  kind:       'primary' | 'class'
}

function StudentProfileView() {
  const { session, profile: authProfile } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, studentName, loading: vasLoading } = useViewerStudent()
  const navigate = useNavigate()
  const fileRef  = useRef<HTMLInputElement>(null)

  const [sp,            setSp]            = useState<StudentProfile | null>(null)
  const [parents,       setParents]       = useState<ParentContact[]>([])
  const [mentors,       setMentors]       = useState<MentorContact[]>([])
  const [counties,      setCounties]      = useState<string[]>([])
  const [loading,       setLoading]       = useState(true)
  const [saving,        setSaving]        = useState(false)
  const [uploading,     setUploading]     = useState(false)
  const [error,         setError]         = useState<string | null>(null)

  const [schoolFair,       setSchoolFair]       = useState<FairDecision | null>(null)
  const [schoolFairDate,   setSchoolFairDate]   = useState('')
  const [districtFair,     setDistrictFair]     = useState<FairDecision | null>(null)
  const [districtFairDate, setDistrictFairDate] = useState('')
  const [countyFair,       setCountyFair]       = useState<FairDecision | null>(null)
  const [countyFairDate,   setCountyFairDate]   = useState('')
  const [countyName,       setCountyName]       = useState('')
  const [countyCustom,     setCountyCustom]     = useState('')
  const [avatarUrl,        setAvatarUrl]        = useState<string | null>(null)

  const [address,       setAddress]       = useState(EMPTY_ADDRESS)
  const [addrSaving,    setAddrSaving]    = useState(false)
  const [addrError,     setAddrError]     = useState<string | null>(null)
  const [addrSaved,     setAddrSaved]     = useState(false)

  useEffect(() => {
    async function load() {
      if (!session || vasLoading) return
      if (!viewerStudentId) { setLoading(false); return }
      const [{ data: profile }, { data: fairs }] = await Promise.all([
        supabase
          .from('student_profile')
          .select('*')
          .eq('student_id', viewerStudentId)
          .maybeSingle(),
        supabase
          .from('science_fair')
          .select('county')
          .not('county', 'is', null)
          .order('county'),
      ])

      if (profile) {
        setSp(profile as StudentProfile)
        setSchoolFair(profile.school_fair ?? null)
        setSchoolFairDate(profile.school_fair_date ?? '')
        setDistrictFair(profile.district_fair ?? null)
        setDistrictFairDate(profile.district_fair_date ?? '')
        setCountyFair(profile.county_fair ?? null)
        setCountyFairDate(profile.county_fair_date ?? '')
        setCountyName(profile.county_name ?? '')
        setAvatarUrl(profile.avatar_url ?? null)
        setAddress({
          address_line1: profile.address_line1 ?? '',
          address_line2: profile.address_line2 ?? '',
          address_city:  profile.address_city  ?? '',
          address_state: profile.address_state ?? '',
          address_zip:   profile.address_zip   ?? '',
        })

        // Parent contact — joined from parent_student_link to users.
        const { data: links } = await supabase
          .from('parent_student_link')
          .select('parent_user_id')
          .eq('student_id', profile.student_id)
        const parentIds = (links ?? []).map(l => l.parent_user_id)
        if (parentIds.length > 0) {
          const { data: parentRows } = await supabase
            .from('users')
            .select('first_name, last_name, email, phone')
            .in('user_id', parentIds)
          setParents(parentRows ?? [])
        }

        // Mentors — primary (lead) from student_mentor + class mentor via
        // the active project's class_id. Both are read-only here.
        const collected: Record<string, MentorContact> = {}

        const { data: leadRows } = await supabase
          .from('student_mentor')
          .select('mentor_id')
          .eq('student_id', profile.student_id)
          .eq('mentor_role', 'lead')
          .eq('status', 'active')
        const primaryIds = (leadRows ?? []).map(r => r.mentor_id)

        const { data: activeProject } = await supabase
          .from('project')
          .select('class_id')
          .eq('student_id', profile.student_id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        let classMentorIds: string[] = []
        if (activeProject?.class_id) {
          const { data: cmRows } = await supabase
            .from('class_mentor')
            .select('user_id')
            .eq('class_id', activeProject.class_id)
          classMentorIds = (cmRows ?? []).map(r => r.user_id)
        }

        const allMentorIds = Array.from(new Set([...primaryIds, ...classMentorIds]))
        if (allMentorIds.length > 0) {
          const { data: mentorRows } = await supabase
            .from('users')
            .select('user_id, first_name, last_name, email, avatar_url')
            .in('user_id', allMentorIds)
          for (const m of mentorRows ?? []) {
            collected[m.user_id] = {
              ...m,
              kind: primaryIds.includes(m.user_id) ? 'primary' : 'class',
            }
          }
          // Order: primary first, then class mentor
          setMentors(Object.values(collected).sort((a, b) => a.kind === 'primary' ? -1 : 1))
        }
      }

      const raw = (fairs ?? []).map((f: any) => f.county as string).filter(Boolean)
      setCounties(Array.from(new Set(raw)).sort())
      setLoading(false)
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, viewerStudentId, vasLoading])

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !sp) return
    setUploading(true)
    setError(null)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${sp.student_id}/avatar.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true })
      if (uploadErr) throw new Error(uploadErr.message)

      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)
      setAvatarUrl(publicUrl)
      await supabase.from('student_profile').update({ avatar_url: publicUrl }).eq('student_id', sp.student_id)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  async function handleSave() {
    if (!sp) return
    setSaving(true)
    setError(null)

    const resolvedCounty = countyCustom.trim() || countyName || null

    const { error: err } = await supabase.from('student_profile').update({
      school_fair:          schoolFair,
      school_fair_date:     schoolFair === 'yes' && schoolFairDate ? schoolFairDate : null,
      district_fair:        districtFair,
      district_fair_date:   districtFair === 'yes' && districtFairDate ? districtFairDate : null,
      county_fair:          countyFair,
      county_fair_date:     countyFair === 'yes' && countyFairDate ? countyFairDate : null,
      county_name:          resolvedCounty,
    }).eq('student_id', sp.student_id)

    setSaving(false)
    if (err) { setError(err.message) } else { navigate(-1) }
  }

  async function saveAddress() {
    if (!sp) return
    const clean = Object.fromEntries(
      Object.entries(address).map(([k, v]) => [k, v.trim() || null]),
    ) as Record<keyof typeof EMPTY_ADDRESS, string | null>
    if (!clean.address_line1 || !clean.address_city || !clean.address_state || !clean.address_zip) {
      setAddrError('Please fill in street, city, state and ZIP.')
      return
    }
    setAddrSaving(true)
    setAddrError(null)
    const { error: err } = await supabase.from('student_profile').update(clean).eq('student_id', sp.student_id)
    setAddrSaving(false)
    if (err) { setAddrError(err.message); return }
    setSp({ ...sp, ...clean })
    setAddrSaved(true)
  }

  async function saveGender(gender: 'male' | 'female') {
    if (!sp) return
    const prev = sp.gender ?? null
    setSp({ ...sp, gender })
    const { error: gErr } = await supabase.from('student_profile').update({ gender }).eq('student_id', sp.student_id)
    if (gErr) setSp({ ...sp, gender: prev })
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (!sp)     return <Box sx={{ p: 4 }}><Typography>Profile not found.</Typography></Box>

  // When staff view a student profile, show the student's name (from the
  // hook), not the staff member's own auth profile.
  const displayName   = viewerIsStudent
                          ? `${authProfile?.first_name ?? ''} ${authProfile?.last_name ?? ''}`.trim()
                          : (studentName ?? '')
  const nameParts     = displayName.split(/\s+/)
  const initials      = viewerIsStudent
                          ? `${authProfile?.first_name?.[0] ?? ''}${authProfile?.last_name?.[0] ?? ''}`
                          : `${nameParts[0]?.[0] ?? ''}${nameParts[1]?.[0] ?? ''}`
  const knownCounties = counties.filter(c => !countyCustom)

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 680 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>My Profile</Typography>

      {/* ── Avatar ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={avatarUrl ?? undefined}
              sx={{ width: 80, height: 80, fontSize: 28, bgcolor: 'primary.main' }}
            >
              {!avatarUrl && initials}
            </Avatar>
            {uploading && (
              <CircularProgress size={80} sx={{ position: 'absolute', top: 0, left: 0 }} />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {gradeLabel(sp.grade)} · {sp.school_name}
            </Typography>
            {viewerIsStudent && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
                <Button
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {avatarUrl ? 'Change photo' : 'Upload photo'}
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── Account details (read-only) ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Account Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            {[
              { label: 'School',   value: sp.school_name  },
              { label: 'City',     value: sp.school_city  },
              { label: 'State',    value: sp.school_state },
              { label: `Grade (Fall ${FALL_YEAR})`, value: `Grade ${sp.grade}` },
              { label: 'Program',  value: sp.program_type.replace('_', ' ') },
              { label: 'Status',   value: sp.subscription_status },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          {viewerIsStudent && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Gender</Typography>
              <ToggleButtonGroup
                exclusive size="small" value={sp.gender ?? null}
                onChange={(_, v) => { if (v) saveGender(v) }}
                sx={{ mt: 0.5, display: 'block' }}
              >
                <ToggleButton value="male">Male</ToggleButton>
                <ToggleButton value="female">Female</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Mentors (read-only) ── */}
      {mentors.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Your Mentor{mentors.length > 1 ? 's' : ''}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {mentors.map(m => {
                const fullName = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email
                const initials = `${m.first_name?.[0] ?? ''}${m.last_name?.[0] ?? ''}`.toUpperCase()
                return (
                  <Box key={m.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={m.avatar_url ?? undefined}
                      sx={{ width: 56, height: 56, fontSize: 18, bgcolor: 'primary.main' }}
                    >
                      {!m.avatar_url && initials}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.kind === 'primary' ? 'Primary Mentor' : 'Class Mentor'}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Parent / Guardian Contact (read-only in MVP) ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Parent / Guardian Contact</Typography>
          {parents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No parent contact on file. Contact your office to add one.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {parents.map((p, i) => {
                const fullName = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim()
                return (
                  <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{fullName || 'Not set'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.email}</Typography>
                    </Box>
                    {p.phone && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.phone}</Typography>
                      </Box>
                    )}
                  </Box>
                )
              })}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                To update parent contact, contact your office.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Home Address ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Home Address</Typography>
          {viewerIsStudent ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Our Ardent Care team uses this to send your family's invoice.
              </Typography>
              {!sp.address_line1 && (
                <Alert severity="info" sx={{ mb: 2 }}>Please add your home address.</Alert>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5 }}>
                {ADDRESS_FIELDS.map(f => (
                  <TextField
                    key={f.key}
                    size="small"
                    label={f.label}
                    required={f.required}
                    value={address[f.key]}
                    onChange={e => { setAddress({ ...address, [f.key]: e.target.value }); setAddrSaved(false) }}
                    autoComplete={f.autoComplete}
                    sx={{ gridColumn: { sm: f.span } }}
                  />
                ))}
              </Box>
              {addrError && <Alert severity="error" sx={{ mt: 2 }}>{addrError}</Alert>}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button variant="contained" onClick={saveAddress} disabled={addrSaving}>
                  {addrSaving ? 'Saving…' : 'Save address'}
                </Button>
                {addrSaved && <Typography variant="body2" color="success.main">Saved</Typography>}
              </Box>
            </>
          ) : sp.address_line1 ? (
            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-line' }}>
              {formatAddress(sp)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">Not added yet.</Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Science Fair Info ── */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Science Fair Schedule</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us show your countdown. You can update this anytime — it's never a blocker.
          </Typography>

          {/* School Fair */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>School Fair</Typography>
            <FairToggle value={schoolFair} onChange={setSchoolFair} />
            {schoolFair === 'yes' && (
              <TextField
                type="date"
                size="small"
                label="School fair date"
                value={schoolFairDate}
                onChange={e => setSchoolFairDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ mt: 1.5, display: 'block', maxWidth: 220 }}
              />
            )}
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* District Fair */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>District Fair</Typography>
            <FairToggle value={districtFair} onChange={setDistrictFair} />
            {districtFair === 'yes' && (
              <TextField
                type="date"
                size="small"
                label="District fair date"
                value={districtFairDate}
                onChange={e => setDistrictFairDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ mt: 1.5, display: 'block', maxWidth: 220 }}
              />
            )}
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* County Fair */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>County Fair</Typography>
            <FairToggle value={countyFair} onChange={setCountyFair} />
            {countyFair !== null && countyFair !== 'no' && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    County
                  </Typography>
                  <Select
                    size="small"
                    value={countyCustom ? '__custom__' : (countyName || '')}
                    onChange={e => {
                      if (e.target.value === '__custom__') {
                        setCountyName('')
                      } else {
                        setCountyName(e.target.value)
                        setCountyCustom('')
                      }
                    }}
                    displayEmpty
                    sx={{ minWidth: 260 }}
                  >
                    <MenuItem value=""><em>Select county…</em></MenuItem>
                    {knownCounties.map(c => (
                      <MenuItem key={c} value={c}>{c} County</MenuItem>
                    ))}
                    <MenuItem value="__custom__">Other (type below)</MenuItem>
                  </Select>
                  {(countyCustom || countyName === '') && (
                    <TextField
                      size="small"
                      placeholder="Type county name…"
                      value={countyCustom}
                      onChange={e => setCountyCustom(e.target.value)}
                      sx={{ mt: 1, display: 'block', maxWidth: 260 }}
                    />
                  )}
                </Box>
                {countyFair === 'yes' && (
                  <TextField
                    type="date"
                    size="small"
                    label="County fair date"
                    value={countyFairDate}
                    onChange={e => setCountyFairDate(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ maxWidth: 220 }}
                  />
                )}
              </Box>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {viewerIsStudent && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

// ── Router ────────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { profile } = useAuth()
  const { viewerIsStudent } = useViewerStudent()

  if (!profile) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>

  // Parents manage their own account (name / email / password) — the generic
  // self-editor, not the student profile view.
  if (profile.role === 'parent') return <AdminProfile />

  // Staff inside the /view-as/:studentId/profile mirror render the student view
  // populated with the impersonated student's data.
  if (!viewerIsStudent || profile.role === 'student') return <StudentProfileView />
  return <AdminProfile />
}
